import { isAccountExplicitLogon } from 'owa-account-shared-mailbox-utils';
import type { MailboxInfo } from 'owa-client-types';
import type { M365Acquisition } from 'owa-m365-acquisitions/lib/types';
import {
    hasExchangeAddIn,
    hasExchangeAddInSharedFolderComposeCommand,
    hasExchangeAddInSharedFolderReadCommand,
} from 'owa-m365-acquisitions/lib/utils/hasExchangeAddIn';
import { hasExtensionAddinSharedFolders } from 'owa-m365-acquisitions/lib/utils/hasExtensionAddIn';

/**
 * Determines if an extension is enabled for a specific mailbox type.
 *
 * This function checks whether an M365 acquisition (extension/add-in) should be enabled
 * based on the mailbox type and configuration. For regular mailboxes and non-explicit
 * logon accounts, extensions are enabled by default. For shared mailboxes and explicit
 * logon accounts, additional checks are performed to determine if the extension supports
 * shared folder operations.
 *
 * @param acquisition - The M365 acquisition object containing extension/add-in information
 * @param mailboxInfo - Information about the mailbox including its type and configuration
 *
 * @returns `true` if the extension should be enabled for the given mailbox type, `false` otherwise
 */
export function isExtensionEnabledForMailboxType(
    acquisition: M365Acquisition,
    mailboxInfo: MailboxInfo
): boolean {
    if (mailboxInfo.type !== 'SharedMailbox' && !isAccountExplicitLogon(mailboxInfo)) {
        return true;
    }
    if (hasExchangeAddIn(acquisition)) {
        return (
            hasExchangeAddInSharedFolderComposeCommand(acquisition) ||
            hasExchangeAddInSharedFolderReadCommand(acquisition)
        );
    }

    return hasExtensionAddinSharedFolders(acquisition);
}
